import secureApiService from '@/services/secureApiService'
import { sanitizeInput } from '@/utils/security'
import { ApplicationPaymentModel } from '@/models/application.model'

interface UploadPaymentResponse {
  success: boolean
  message?: string
  data?: any
}

interface VerifyPaymentResponse {
  success: boolean
  message?: string
  data?: {
    action?: 'confirmed' | 'rejected'
    allocation?: {
      totalAmount?: number
      totalPaid?: number
      newStatus?: string
      leftover?: number
    }
  }
}

export interface PaymentProofPayload {
  amount: number
  reference: string
  file: File
}

export interface PaymentVerificationPayload {
  status: 'confirmed' | 'rejected'
  note?: string
}

export interface PaymentVerificationResult {
  action?: 'confirmed' | 'rejected'
  allocation?: {
    totalAmount?: number
    totalPaid?: number
    newStatus?: string
    leftover?: number
  }
}

class PaymentsService {
  private basePath = '/payments/applications'

  async uploadPaymentProof(applicationId: string, payload: PaymentProofPayload): Promise<ApplicationPaymentModel> {
    const trimmedId = sanitizeInput(applicationId)
    const formData = new FormData()
    formData.append('amount', sanitizeInput(String(payload.amount)))
    formData.append('reference', sanitizeInput(payload.reference))
    formData.append('file', payload.file)

    const response = await secureApiService.postFormData<UploadPaymentResponse>(
      `${this.basePath}/${encodeURIComponent(trimmedId)}/upload`,
      formData,
      { requireAuth: true },
    )

    const body = response.data
    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to upload payment proof')
    }

    return ApplicationPaymentModel.fromJSON(body.data)
  }

  async verifyPaymentProof(paymentId: string, payload: PaymentVerificationPayload): Promise<PaymentVerificationResult> {
    const trimmedId = sanitizeInput(paymentId)
    const requestBody: Record<string, string> = {
      status: sanitizeInput(payload.status),
    }

    if (payload.note) {
      requestBody.note = sanitizeInput(payload.note)
    }

    const response = await secureApiService.put<VerifyPaymentResponse>(
      `/payments/${encodeURIComponent(trimmedId)}/verify`,
      requestBody,
      { requireAuth: true },
    )

    const body = response.data
    if (!body?.success) {
      throw new Error(body?.message || 'Unable to verify payment')
    }

    return {
      action: body.data?.action,
      allocation: body.data?.allocation,
    }
  }
}

export const paymentsService = new PaymentsService()
